import { API_BASE_URL } from '../constants';

/**
 * The client for the DynaMan simulation endpoints.
 *
 * <h2>The stream uses `fetch`, not `EventSource`, and that is forced</h2>
 *
 * `/api/simulation/stream` is an authenticated endpoint, and `EventSource` cannot send an
 * `Authorization` header — it has no options argument at all. The alternatives were to make the
 * endpoint public (an unbounded read of a symbol's whole history, holding a server thread, open to
 * anyone) or to pass the bearer token as a query parameter (where it lands in access logs and
 * browser history). Neither is acceptable for a token, so the stream is read with `fetch` and the
 * SSE framing is parsed here.
 *
 * <p>The cost of that choice is this file: about forty lines of framing that `EventSource` would
 * have done. The benefit is that the endpoint is protected exactly like every other one.
 *
 * <h2>Events are dispatched by name, and an unknown name is ignored</h2>
 *
 * The server may gain an event type before this ships. Ignoring unknown names means a new one is a
 * no-op rather than a crash mid-run, which for something a human is watching matters more than
 * strictness.
 */

/** Wire event names. Kept here so a rename is one edit rather than a search through JSX. */
export const SIM_EVENTS = {
  START: 'start',
  BAR: 'bar',
  TRANSITION: 'transition',
  REJECTION: 'rejection',
  INTENT: 'intent',
  WARNING: 'warning',
  WINDOW: 'window',
  COMPLETE: 'complete',
  ERROR: 'error',
};

/**
 * Turns simulation options into a query string.
 *
 * Only values the caller actually set are sent, so every unspecified rule stays at the backend's
 * baseline. Sending the defaults explicitly would freeze today's values into the client, and the
 * next time a default changed the UI would silently keep overriding it with the old one.
 */
/**
 * The engine modes the backend will accept, mirroring {@code EngineMode.java}.
 *
 * <p>Declared here rather than imported from `replaySession`, which is the Workbench's module and
 * would make this utility depend on a dialog. Pinned against the Java enum by a test, because
 * Java cannot hand an enum to JavaScript and a mode missing from this set is dropped in silence.
 */
export const ENGINE_MODE_VALUES = new Set(['LEGACY', 'DIRECT', 'TIMING_ENGINE', 'PTB_TIMING_ENGINE']);

export const buildSimulationQuery = ({
  symbol,
  timeframes,
  maxBars,
  delayMs,
  breakout,
  macroFast,
  macroSlow,
  ema,
  floor,
  volumeMultiple,
  bailoutOn,
  signalProvider,
  exitStrategy,
  stopStrategy,
  stopAtrBuffer,
  disableStructuralEntry,
  ctsDirection,
  ctsVectorLookback,
  ctsPriceConfirmation,
  ctsConfirmationWindow,
  ctsConfirmOnClose,
  stopPointOffset,
  tpPeakWidth,
  tpProminence,
  tpExtremeDuration,
  tpDriftFilter,
  ctsReentry,
  targetPta,
  targetPtb,
  levelMinR,
  /**
   * THE MACRO GATE AND THE VOLUME FILTER, and their absence here is why the toggle did nothing.
   *
   * <p>This builder destructures a FIXED list and silently drops anything not on it. The dialog
   * passed the gate on all three run paths from the day the switch shipped; the parameter never
   * left the browser, so the backend received no value, applied its default, and every scorecard
   * came back identical however the switch was set. It fails in the quietest way this codebase
   * has: no error, no warning, no 400, just a correct-looking answer to a question nobody asked.
   *
   * <p>`macroGate` is the canonical spelling, and it is ONE parameter on purpose. There are two
   * engine flags behind it -- `timeBandBypassMacroVeto` for PTA and PTB, `ctsBypassMacroVeto` for
   * CTS -- and the backend routes to whichever the selected provider reads. A parameter per flag
   * would put a provider branch in the client, and a branch in the client is how three run paths
   * came to send three different strategies from one dialog.
   */
  macroGate,
  bandVolumeConfluence,
  /**
   * THE CTS EXIT SANDBOX. One destructured key per lever, because this builder drops
   * silently whatever is not on the list -- the defect that made the macro gate look
   * broken for a fortnight. Sixteen names is the price of that never happening again.
   */
  ctsExitSandbox,
  ctsSignalToSignal,
  ctsRsiExit,
  ctsRsiPeriod,
  ctsRsiOverbought,
  ctsRsiOversold,
  ctsMacdExit,
  ctsMacdFast,
  ctsMacdSlow,
  ctsMacdSignal,
  ctsTrendlineExit,
  ctsTrendlineAtrBuffer,
  ctsTrendlineFractalWidth,
  // Added with its own switch. A lever absent from this list is DROPPED silently --
  // the defect that made the macro gate look broken for a fortnight, and it reached
  // this one too: the first run after adding the toggle sent no value at all.
  ctsBreathableStopEnabled,
  ctsInitialStopAtr,
  ctsMaxHoldBars,
  ctsConsecutiveReentry,

  // PROFIT PROTECTION. In this list because a lever absent from it is DROPPED SILENTLY -- no
  // error, no warning, no 400, just a correct-looking answer to a question nobody asked. That
  // has happened here three times now (bandMacroGate, bandVolumeConfluence,
  // ctsBreathableStopEnabled), which is why every new lever goes in in the same edit.
  breakevenLockEnabled,
  breakevenLockActivationPct,
  breakevenLockOffsetPct,
  acceleratedRatchetEnabled,
  ratchetActivationPct,
  ratchetTighteningPct,

  /**
   * ADVANCED TRADE MANAGEMENT, and this list is where the Phase 6C wiring first went wrong.
   *
   * <p>The four levers reached the dialog, the session, the request builder and the backend's own
   * parameter list, and were then DROPPED HERE — because this builder destructures a fixed list
   * and silently discards everything else. The run came back 200 with a correct-looking ledger of
   * the control configuration. Found by reading the outgoing URL in a browser, which is the fourth
   * time that has been the only instrument that would have found it.
   */
  fanExit,
  fanReload,
  atrTrailingStop,
  maxBarsInTrade,
  /*
   * THE TUNING PARAMETERS, added to this list IN THE SAME EDIT as the controls that send them.
   * That sentence is the whole lesson of the four levers this destructure has silently dropped:
   * bandMacroGate, bandVolumeConfluence, ctsBreathableStopEnabled and the Phase 6 four. Every
   * one reached a switch, a state and a request field, and died here with no error.
   */
  fanFractalWidth,
  reloadEma,
  /*
   * PHASE 10's two, added to this list in the same edit as the controls that send them -- which is
   * the only thing that has ever stopped a lever dying here. `reloadMinRetracement` and
   * `reloadMaxRetracement` are deliberately NOT in the list any more: the band is gone and the
   * backend refuses them, so a caller still passing one gets a 400 rather than a silent default.
   */
  structuralTolerance,
  sameBarEntry,
  crossoverRebound,
  maxTranches,
  chargedContinuation,
  continuationTrendSma,
  engineMode,
  directBreakevenR,
  enginePreset,
  committeeOverride,
  committeeMinVotes,

  /** Comma-separated engine list for a COMPOSITE run; ignored by every other provider. */
  compositeEngines,

  startingEquity,
  riskPerTradePct,
  commissionPerShare,
  slippageBps,
  allowFractionalShares,
  maxLeverage,
} = {}) => {
  const params = new URLSearchParams();
  if (symbol) params.set('symbol', symbol);
  if (timeframes && timeframes.length) params.set('timeframes', timeframes.join(','));
  // Sent even when ZERO, because zero is the request: "report every bar". A falsy check would
  // drop it and silently restore the windowed default, which is the defect it exists to fix.
  if (maxBars !== undefined && maxBars !== null) params.set('maxBars', String(maxBars));
  if (delayMs !== undefined && delayMs !== null) params.set('delayMs', String(delayMs));
  if (breakout) params.set('breakout', String(breakout));
  if (macroFast) params.set('macroFast', String(macroFast));
  if (macroSlow) params.set('macroSlow', String(macroSlow));
  if (ema) params.set('ema', String(ema));
  if (floor !== undefined && floor !== null && floor !== '') params.set('floor', String(floor));
  if (volumeMultiple) params.set('volumeMultiple', String(volumeMultiple));
  if (bailoutOn && bailoutOn.length) params.set('bailoutOn', bailoutOn.join(','));
  // The provider is named, not a boolean. `trendPulse=true` was the original flag, back when
  // TrendPulse was the only provider; the backend still accepts it for bundles published before
  // this shipped, but nothing here sends it any more.
  if (signalProvider) params.set('signalProvider', signalProvider);
  if (exitStrategy) params.set('exitStrategy', exitStrategy);
  if (stopStrategy) params.set('stopStrategy', stopStrategy);
  if (stopAtrBuffer !== undefined && stopAtrBuffer !== null) {
    params.set('stopAtrBuffer', String(stopAtrBuffer));
  }
  if (disableStructuralEntry) params.set('disableStructuralEntry', 'true');

  // ADVANCED TRADE MANAGEMENT. The three booleans go out in BOTH directions, for the reason
  // spelled out under profit protection below: the backend builds its explicit-lever set from
  // nullability, so a dropped `false` reads as "not set".
  //
  // `maxBarsInTrade` is sent whenever it is a positive number. Zero is the engine's own "no
  // limit" AND its default, so omitting it and sending 0 are identical in effect -- and omitting
  // keeps an untouched section off the wire entirely, which is what makes a captured URL readable.
  if (fanExit !== undefined && fanExit !== null) {
    params.set('fanExit', fanExit ? 'true' : 'false');
  }
  if (fanReload !== undefined && fanReload !== null) {
    params.set('fanReload', fanReload ? 'true' : 'false');
  }
  if (atrTrailingStop !== undefined && atrTrailingStop !== null) {
    params.set('atrTrailingStop', atrTrailingStop ? 'true' : 'false');
  }
  if (Number.isFinite(Number(maxBarsInTrade)) && Number(maxBarsInTrade) > 0) {
    params.set('maxBarsInTrade', String(Math.floor(Number(maxBarsInTrade))));
  }

  // THE TUNING PARAMETERS. Sent when they are a usable number and omitted otherwise, which is
  // what makes "leave the field alone" mean the engine's own default rather than a zero.
  //
  // The fractal width is floored and must be positive: a half-bar pivot does not exist, and the
  // builder refuses a non-positive one with an exception rather than a silent clamp.
  if (Number.isFinite(Number(fanFractalWidth)) && Number(fanFractalWidth) > 0) {
    params.set('fanFractalWidth', String(Math.floor(Number(fanFractalWidth))));
  }
  if (Number.isFinite(Number(reloadEma)) && Number(reloadEma) > 0) {
    params.set('reloadEma', String(Math.floor(Number(reloadEma))));
  }
  // A MULTIPLE OF ATR: unfloored, uncapped, and sent only when positive. Zero is the strict rule
  // and the engine's own default, so omitting it and sending 0 are identical in effect.
  if (Number.isFinite(Number(structuralTolerance)) && Number(structuralTolerance) > 0) {
    params.set('structuralTolerance', String(Number(structuralTolerance)));
  }
  // 1..3, and sent whenever it is a usable number -- including 1, which is a real setting and
  // not "unset". Anything outside the range is dropped rather than clamped: the backend refuses
  // it with a 400, which is a better answer than a book quietly a third smaller than requested.
  if (Number.isFinite(Number(maxTranches))
      && Number(maxTranches) >= 1 && Number(maxTranches) <= 3) {
    params.set('maxTranches', String(Math.floor(Number(maxTranches))));
  }
  // A WHOLE NUMBER OF BARS, sent only when positive. Zero is "leave the engine's own 50 alone",
  // and the engine REFUSES a zero of its own -- so sending one would be a 400 for a field the
  // reader simply had not filled in.
  // THE ENGINE, sent whenever it is one of the KNOWN modes -- including LEGACY, which is a real
  // setting rather than "unset".
  //
  // A SET, not `=== 'LEGACY' || === 'DIRECT'`. That form was correct at two modes and would have
  // silently dropped the third: the request would succeed, the backend would default to Legacy,
  // and the dropdown would still read "Timing Engine". The same two-way shape that ran the wrong
  // market when Market gained a third value, in the one hop of this app that drops what it does
  // not name.
  if (ENGINE_MODE_VALUES.has(engineMode)) {
    params.set('engineMode', engineMode);
  }
  // SENT WHENEVER IT WAS DECIDED, including false -- the backend defaults it ON, so `false` is the
  // only value that can express "no preset" and dropping it would silently run one.
  // SENT ONLY WHEN IT IS A BOOLEAN. Absent means "unset", which is what lets the backend
  // resolve the per-mode default; sending `false` for an untouched control would switch the
  // committee off for PTB + Timing, the one mode whose default is on.
  if (typeof committeeOverride === 'boolean') {
    params.set('committeeOverride', String(committeeOverride));
  }
  // ZERO IS UNSET, so it is not sent -- the backend then applies its own default of 3. Sending
  // 0 would be refused outright, because a threshold of zero holds every stop.
  if (committeeMinVotes > 0) {
    params.set('committeeMinVotes', String(committeeMinVotes));
  }
  if (typeof enginePreset === 'boolean') {
    params.set('enginePreset', String(enginePreset));
  }
  if (Number.isFinite(Number(directBreakevenR)) && Number(directBreakevenR) > 0) {
    params.set('directBreakevenR', String(Number(directBreakevenR)));
  }
  if (Number.isFinite(Number(continuationTrendSma)) && Number(continuationTrendSma) >= 1) {
    params.set('continuationTrendSma', String(Math.floor(Number(continuationTrendSma))));
  }
  [['sameBarEntry', sameBarEntry], ['crossoverRebound', crossoverRebound],
    ['chargedContinuation', chargedContinuation]]
    .forEach(([key, value]) => {
      if (value !== undefined && value !== null) params.set(key, value ? 'true' : 'false');
    });

  // PROFIT PROTECTION, sent EXPLICITLY in both directions. The backend builds its explicit-lever
  // set from parameter NULLABILITY, so a falsy check that dropped `false` would mark the lever
  // unset and hand the decision back to the engine default -- the same value today, and a
  // different one the moment that default changes. The numeric levers are sent whenever defined,
  // zero included, because zero is a legitimate setting for both of them.
  if (breakevenLockEnabled !== undefined && breakevenLockEnabled !== null) {
    params.set('breakevenLockEnabled', breakevenLockEnabled ? 'true' : 'false');
  }
  if (breakevenLockActivationPct !== undefined && breakevenLockActivationPct !== null
      && breakevenLockActivationPct !== '') {
    params.set('breakevenLockActivationPct', String(breakevenLockActivationPct));
  }
  if (breakevenLockOffsetPct !== undefined && breakevenLockOffsetPct !== null
      && breakevenLockOffsetPct !== '') {
    params.set('breakevenLockOffsetPct', String(breakevenLockOffsetPct));
  }
  if (acceleratedRatchetEnabled !== undefined && acceleratedRatchetEnabled !== null) {
    params.set('acceleratedRatchetEnabled', acceleratedRatchetEnabled ? 'true' : 'false');
  }
  if (ratchetActivationPct !== undefined && ratchetActivationPct !== null
      && ratchetActivationPct !== '') {
    params.set('ratchetActivationPct', String(ratchetActivationPct));
  }
  if (ratchetTighteningPct !== undefined && ratchetTighteningPct !== null
      && ratchetTighteningPct !== '') {
    params.set('ratchetTighteningPct', String(ratchetTighteningPct));
  }
  // Only meaningful for COMPOSITE. Sent whenever present rather than branching on the provider
  // here: the backend ignores it for every other mode, and a client-side provider branch is how
  // three run paths came to send three different strategies from one dialog.
  if (compositeEngines) params.set('compositeEngines', compositeEngines);
  // SENT EXPLICITLY WHEN FALSE TOO, unlike the flags above it. A toggle a viewer has switched OFF
  // must reach the server as off rather than as absent, because "absent" means the engine default
  // — which is also off today and would stop being the same thing the moment that changes.
  if (ctsDirection) params.set('ctsDirection', ctsDirection);
  // THE CTS ENTRY GATE. The two booleans travel even when FALSE, for the reason the comment above
  // gives and which this list has now punished eight separate levers for ignoring: the backend
  // derives its explicit-lever set from NULLABILITY, so a dropped `false` reads as "not set" and
  // the engine default silently wins. The numbers are sent when finite, zero included -- a zero
  // point-offset is the bare wick and is a real setting.
  if (ctsPriceConfirmation !== undefined && ctsPriceConfirmation !== null) {
    params.set('ctsPriceConfirmation', ctsPriceConfirmation ? 'true' : 'false');
  }
  if (ctsConfirmOnClose !== undefined && ctsConfirmOnClose !== null) {
    params.set('ctsConfirmOnClose', ctsConfirmOnClose ? 'true' : 'false');
  }
  if (Number.isFinite(ctsConfirmationWindow)) {
    params.set('ctsConfirmationWindow', String(ctsConfirmationWindow));
  }
  if (Number.isFinite(ctsVectorLookback)) {
    params.set('ctsVectorLookback', String(ctsVectorLookback));
  }
  if (Number.isFinite(stopPointOffset)) {
    params.set('stopPointOffset', String(stopPointOffset));
  }
  // TrendPulse's shape-detection levers. Sent when set, INCLUDING zero-ish values, because a
  // numeric lever's falsy cases are legitimate settings rather than "unset".
  if (Number.isFinite(tpPeakWidth)) params.set('tpPeakWidth', String(tpPeakWidth));
  if (Number.isFinite(tpProminence)) params.set('tpProminence', String(tpProminence));
  if (Number.isFinite(tpExtremeDuration)) {
    params.set('tpExtremeDuration', String(tpExtremeDuration));
  }
  if (tpDriftFilter !== undefined && tpDriftFilter !== null) {
    params.set('tpDriftFilter', tpDriftFilter ? 'true' : 'false');
  }
  if (ctsReentry !== undefined && ctsReentry !== null) {
    params.set('ctsReentry', ctsReentry ? 'true' : 'false');
  }
  // THE TWO STRUCTURAL TARGETS, independently. PTA is the first swing level beyond the entry and
  // PTB the second, and the three live combinations are three different strategies -- scale out
  // and trail, hold to the second level, or both. Sent as explicit booleans rather than omitted
  // when false, because "off" is a configuration a measurement needs to be able to request.
  // BOTH SENT EXPLICITLY, including when false. A falsy check would drop `false`, and `false` is
  // the whole point here: it is how a reader turns the gate back OFF after trying it, and the
  // backend builds its explicit-lever set from parameter NULLABILITY -- so an omitted `false`
  // would hand the lever back to the provider profile, which is the same bug in a new costume.
  if (macroGate !== undefined && macroGate !== null) {
    params.set('macroGate', macroGate ? 'true' : 'false');
  }
  if (bandVolumeConfluence !== undefined && bandVolumeConfluence !== null) {
    params.set('bandVolumeConfluence', bandVolumeConfluence ? 'true' : 'false');
  }
  // The CTS sandbox. Booleans go out even when FALSE (that is how a reader switches a
  // philosophy back off), numbers on a finiteness check (zero is a real setting -- a
  // max-hold of 0 disables the time ejector, and a 0 ATR buffer is the bare line).
  if (ctsExitSandbox !== undefined && ctsExitSandbox !== null) {
    params.set('ctsExitSandbox', ctsExitSandbox ? 'true' : 'false');
  }
  if (ctsSignalToSignal !== undefined && ctsSignalToSignal !== null) {
    params.set('ctsSignalToSignal', ctsSignalToSignal ? 'true' : 'false');
  }
  if (ctsRsiExit !== undefined && ctsRsiExit !== null) {
    params.set('ctsRsiExit', ctsRsiExit ? 'true' : 'false');
  }
  if (Number.isFinite(ctsRsiPeriod)) params.set('ctsRsiPeriod', String(ctsRsiPeriod));
  if (Number.isFinite(ctsRsiOverbought)) params.set('ctsRsiOverbought', String(ctsRsiOverbought));
  if (Number.isFinite(ctsRsiOversold)) params.set('ctsRsiOversold', String(ctsRsiOversold));
  if (ctsMacdExit !== undefined && ctsMacdExit !== null) {
    params.set('ctsMacdExit', ctsMacdExit ? 'true' : 'false');
  }
  if (Number.isFinite(ctsMacdFast)) params.set('ctsMacdFast', String(ctsMacdFast));
  if (Number.isFinite(ctsMacdSlow)) params.set('ctsMacdSlow', String(ctsMacdSlow));
  if (Number.isFinite(ctsMacdSignal)) params.set('ctsMacdSignal', String(ctsMacdSignal));
  if (ctsTrendlineExit !== undefined && ctsTrendlineExit !== null) {
    params.set('ctsTrendlineExit', ctsTrendlineExit ? 'true' : 'false');
  }
  if (Number.isFinite(ctsTrendlineAtrBuffer)) params.set('ctsTrendlineAtrBuffer', String(ctsTrendlineAtrBuffer));
  if (Number.isFinite(ctsTrendlineFractalWidth)) params.set('ctsTrendlineFractalWidth', String(ctsTrendlineFractalWidth));
  if (ctsBreathableStopEnabled !== undefined && ctsBreathableStopEnabled !== null) {
    params.set('ctsBreathableStopEnabled', ctsBreathableStopEnabled ? 'true' : 'false');
  }
  if (Number.isFinite(ctsInitialStopAtr)) params.set('ctsInitialStopAtr', String(ctsInitialStopAtr));
  if (Number.isFinite(ctsMaxHoldBars)) params.set('ctsMaxHoldBars', String(ctsMaxHoldBars));
  if (ctsConsecutiveReentry !== undefined && ctsConsecutiveReentry !== null) {
    params.set('ctsConsecutiveReentry', ctsConsecutiveReentry ? 'true' : 'false');
  }
  if (targetPta !== undefined && targetPta !== null) {
    params.set('targetPta', targetPta ? 'true' : 'false');
  }
  if (targetPtb !== undefined && targetPtb !== null) {
    params.set('targetPtb', targetPtb ? 'true' : 'false');
  }
  // Zero is a REAL setting (accept any level however close), so this is a finiteness check and
  // not a truthiness one.
  if (Number.isFinite(levelMinR)) params.set('levelMinR', String(levelMinR));
  // Account levers. Sent only when set, so an omitted account means the FRICTIONLESS default on
  // the server rather than a starting equity this file invented.
  if (startingEquity) params.set('startingEquity', String(startingEquity));
  if (riskPerTradePct) params.set('riskPerTradePct', String(riskPerTradePct));
  if (commissionPerShare !== undefined && commissionPerShare !== null) {
    params.set('commissionPerShare', String(commissionPerShare));
  }
  if (slippageBps !== undefined && slippageBps !== null) {
    params.set('slippageBps', String(slippageBps));
  }
  if (allowFractionalShares) params.set('allowFractionalShares', 'true');
  if (maxLeverage) params.set('maxLeverage', String(maxLeverage));
  return params.toString();
};

/**
 * THE REQUEST BODY for a run on the caller's own price series.
 *
 * <h2>Derived from the query string, deliberately</h2>
 *
 * The obvious implementation is a second object literal naming every lever. That would put the
 * lever vocabulary in two places in THIS file as well as two in the backend, and the defect it
 * invites is the one this codebase has now paid for four times: a lever added to the query and
 * missed on the body is not an error. It is silently dropped, the backend applies its default, and
 * the answer comes back looking correct.
 *
 * <p>So the body is built by running {@link buildSimulationQuery} and reading its output back. The
 * levers a custom run sends are therefore EXACTLY the levers an ordinary run sends, by
 * construction rather than by review — a new lever reaches both paths the moment it reaches the
 * destructure list, and there is no second list to forget.
 *
 * <p>Every value arrives as a string, which is what a query string is made of. Jackson coerces
 * them to the record's declared types (`"35"` to Integer, `"true"` to Boolean) — scalar coercion
 * is on by default — so the typed `SimulationLevers` record is what the backend actually sees.
 *
 * <p>`maxBars` and `delayMs` are LIFTED OUT of the levers. They are per-endpoint controls rather
 * than strategy settings, so on the POST side they are top-level fields of the request; left among
 * the levers they would be dropped silently, because `SimulationLevers` has no component for
 * either and ignores what it does not recognise.
 */
export const buildSimulationBody = (options = {}) => {
  const { customSeries, ...rest } = options;
  const levers = Object.fromEntries(new URLSearchParams(buildSimulationQuery(rest)));

  const body = { data: customSeries };
  if (levers.maxBars !== undefined) {
    body.maxBars = Number(levers.maxBars);
    delete levers.maxBars;
  }
  if (levers.delayMs !== undefined) {
    body.delayMs = Number(levers.delayMs);
    delete levers.delayMs;
  }
  // THE SYMBOL IS DISCARDED BY THE SERVER whatever is sent -- a custom run is always labelled
  // CUSTOM_EPHEMERAL, so no caller-chosen identifier reaches the run header, the logs or the
  // activity record. It is dropped here as well so the request does not even carry it: a value
  // that travels and is ignored still travels, and `Z-Custom` is a menu entry rather than
  // something the engine should be told about.
  delete levers.symbol;
  body.levers = levers;
  return body;
};

/** Whether this run carries its own data, and so must be POSTed rather than fetched. */
const isCustomRun = (options) =>
  Boolean(options && options.customSeries && options.customSeries.bars
    && options.customSeries.bars.length);

/** Runs a whole simulation and resolves with the ledger. */
export const runFullSimulation = async (api, options) => {
  // THE SWITCH IS THE PAYLOAD, never a symbol. A reserved ticker string would be a comparison in
  // every layer that handles one; here the presence of a parsed series decides, in one place, and
  // the backend makes the same decision from the HTTP method it received.
  if (isCustomRun(options)) {
    const { data } = await api.post('/api/simulation/full', buildSimulationBody(options));
    return data;
  }
  const { data } = await api.get(`/api/simulation/full?${buildSimulationQuery(options)}`);
  return data;
};

/**
 * The bar-by-bar timeline, for the client-side replay.
 *
 * <p>The same run as {@link runFullSimulation} and the same query, plus the frames. It is a plain
 * GET rather than the SSE stream because the CLOCK belongs to the browser: pacing on the server
 * makes the speed unchangeable mid-run, pausing equivalent to dropping the connection, and
 * stepping backwards impossible.
 *
 * <p>The response is bounded by the same 2,000-bar window the stream uses — roughly half a
 * megabyte, against the forty a 169,144-bar series would produce.
 */
export const fetchReplayTimeline = async (api, options) => {
  if (isCustomRun(options)) {
    const { data } = await api.post('/api/simulation/timeline', buildSimulationBody(options));
    return data;
  }
  const { data } = await api.get(`/api/simulation/timeline?${buildSimulationQuery(options)}`);
  return data;
};

/**
 * Splits an SSE buffer into complete events, returning the unconsumed tail.
 *
 * Exported for its own test. Chunk boundaries fall wherever the network puts them, so the case that
 * matters is a `data:` line arriving split in half — handled by never consuming past the last blank
 * line and handing the remainder back to the caller.
 */
export const parseSseBuffer = (buffer) => {
  const events = [];
  const boundary = buffer.lastIndexOf('\n\n');
  if (boundary < 0) return { events, rest: buffer };

  const complete = buffer.slice(0, boundary);
  for (const block of complete.split('\n\n')) {
    if (!block.trim()) continue;
    let name = 'message';
    const dataLines = [];
    for (const line of block.split('\n')) {
      if (line.startsWith('event:')) name = line.slice(6).trim();
      else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
    }
    if (dataLines.length) events.push({ name, data: dataLines.join('\n') });
  }
  return { events, rest: buffer.slice(boundary + 2) };
};

/**
 * Streams a stepped simulation.
 *
 * @param getToken async () => bearer token
 * @param handlers { onStart, onBar, onTransition, onRejection, onIntent, onWarning, onComplete,
 *                 onError }
 * @param signal   an AbortSignal; aborting stops the run and releases the server's thread
 */
export const streamSimulation = async (getToken, options, handlers = {}, signal) => {
  const token = await getToken();
  // A POST that answers with Server-Sent Events is unusual and is the only shape available here.
  // The GET variant already had to be read with `fetch` because EventSource cannot set an
  // Authorization header; that same machinery is what lets the custom variant send a body at all.
  const custom = isCustomRun(options);
  const response = await fetch(
    custom
      ? `${API_BASE_URL}/api/simulation/stream`
      : `${API_BASE_URL}/api/simulation/stream?${buildSimulationQuery(options)}`,
    {
      method: custom ? 'POST' : 'GET',
      headers: custom
        ? {
          Accept: 'text/event-stream',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        }
        : { Accept: 'text/event-stream', Authorization: `Bearer ${token}` },
      body: custom ? JSON.stringify(buildSimulationBody(options)) : undefined,
      signal,
    }
  );

  if (!response.ok || !response.body) {
    const text = await response.text().catch(() => '');
    throw new Error(`Simulation stream failed (${response.status}). ${text.slice(0, 300)}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    const { events, rest } = parseSseBuffer(buffer);
    buffer = rest;

    for (const event of events) {
      let payload;
      try {
        payload = JSON.parse(event.data);
      } catch {
        // A frame we cannot parse is skipped rather than fatal. Losing one bar of a visualisation
        // is not worth ending a run a human is watching.
        continue;
      }
      dispatch(event.name, payload, handlers);
    }
  }
};

const dispatch = (name, payload, handlers) => {
  switch (name) {
    case SIM_EVENTS.START:
      handlers.onStart?.(payload);
      break;
    case SIM_EVENTS.BAR:
      handlers.onBar?.(payload);
      break;
    case SIM_EVENTS.TRANSITION:
      handlers.onTransition?.(payload);
      break;
    case SIM_EVENTS.REJECTION:
      handlers.onRejection?.(payload);
      break;
    case SIM_EVENTS.INTENT:
      handlers.onIntent?.(payload);
      break;
    case SIM_EVENTS.WARNING:
      handlers.onWarning?.(payload?.warning);
      break;
    case SIM_EVENTS.WINDOW:
      handlers.onWindow?.(payload);
      break;
    case SIM_EVENTS.COMPLETE:
      handlers.onComplete?.(payload);
      break;
    case SIM_EVENTS.ERROR:
      handlers.onError?.(payload?.error || 'The simulation reported an error');
      break;
    default:
      break;   // unknown event names are ignored on purpose
  }
};

/** Percentages, signed, to two places. `null`/`undefined` become an em dash rather than "NaN%". */
export const pct = (value, places = 2) =>
  value === null || value === undefined || Number.isNaN(value)
    ? '—'
    : `${value >= 0 ? '+' : ''}${value.toFixed(places)}%`;

/** Prices to four places, with no currency symbol — the engine is denomination-agnostic. */
export const px = (value) =>
  value === null || value === undefined || Number.isNaN(value) ? '—' : value.toFixed(4);

/** An absent profit factor is an ABSENT denominator, not a zero and not an infinity. */
export const profitFactorText = (value) =>
  value === null || value === undefined ? 'n/a (no losing trades)' : value.toFixed(2);

/** The engine's phases, in lifecycle order, with a colour each for the HUD. */
export const STATE_COLORS = {
  CASH: 'default',
  IGNITION: 'warning',
  EXPANSION: 'success',
  RETRACEMENT: 'info',
  EXHAUSTION: 'error',
};

/** Rejection reasons, in the order they are checked, with display colours. */
export const REJECTION_COLORS = {
  VETO_MACRO_TREND: 'error',
  VETO_MACRO_REGIME_UNKNOWN: 'default',
  VETO_VOLUME_CONFIRMATION: 'warning',
  VETO_POSITION_ALREADY_OPEN: 'info',
  VETO_NO_USABLE_STOP: 'secondary',
  // The only one raised by a PROVIDER rather than by a gate, and the only one that is not a veto:
  // a timing node armed and price never confirmed it. Default rather than error or warning,
  // because nothing went wrong -- the filter did the job it exists to do, and colouring it as a
  // failure would read as the engine refusing trades it meant to take.
  BYPASSED_NO_PRICE_CONFIRMATION: 'default',
  // The liberal tier's two outputs. A WARNING is information and gets a visible colour; the
  // NOISE bucket is the filter working and is muted, because at orb 3.0 it is most of the tier
  // and a loud colour would make the audit unreadable at exactly the wrong moment.
  LIBERAL_EARLY_WARNING: 'warning',
  LIBERAL_ISOLATED_NOISE: 'default',
};
